import React, { useMemo } from "react"
import { Table, Tag, Space, Typography, message } from "@mimber/ui-web"
import {
  DollarOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from "@mimber/ui-web"
import { useDuesInvoices } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { InvoiceWithRelationsDto } from "@mimber/schema"
import { InvoiceStatus } from "@mimber/schema"
import { formatDateUtc } from "@mimber/shared"
import { formatCurrency } from "@/utils"
import { TableExportButton } from "@/components/tables/TableExportButton"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import type { DuesInvoicesTableProps } from "./DuesInvoicesTable.types"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "default",
  OPEN: "blue",
  PAID: "green",
  OVERDUE: "red",
  VOID: "gray",
}

export const DuesInvoicesTable: React.FunctionComponent<
  DuesInvoicesTableProps
> = ({ onRowClick, className, ...tableProps }) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "dues-invoices",
      defaultQueryParams: {
        page: 1,
        limit: 10,
      },
    })

  const { data: response, isLoading, error } = useDuesInvoices(queryParams)

  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load dues invoices: ${error.message}`)
    }
  }, [error])

  const columns: ColumnsType<InvoiceWithRelationsDto> = useMemo(
    () => [
      {
        title: "Invoice #",
        dataIndex: "invoiceNumber",
        key: "invoiceNumber",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        ...getColumnSearchProps("Search invoice number"),
      },
      {
        title: "Member",
        key: "member",
        render: (_: unknown, record: InvoiceWithRelationsDto) => {
          const user = record.membership?.user
          if (!user) return <Text type="secondary">—</Text>
          return (
            <Space>
              <UserOutlined />
              <Text>
                {user.firstName} {user.lastName}
              </Text>
            </Space>
          )
        },
      },
      {
        title: "Amount",
        key: "totalAmount",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (_: unknown, record: InvoiceWithRelationsDto) => (
          <Space>
            <DollarOutlined />
            <Text>{formatCurrency(record.totalAmount)}</Text>
          </Space>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>
        ),
        filters: Object.values(InvoiceStatus).map((status) => ({
          text: status,
          value: status,
        })),
      },
      {
        title: "Due Date",
        dataIndex: "dueAt",
        key: "dueAt",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (dueAt: string | null) => (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTableDate(dueAt)}</Text>
          </Space>
        ),
      },
      {
        title: "Issued",
        dataIndex: "issuedAt",
        key: "issuedAt",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (issuedAt: string | null) => (
          <Text type="secondary">{formatTableDate(issuedAt)}</Text>
        ),
      },
    ],
    []
  )

  const handleRowClick = (record: InvoiceWithRelationsDto) => {
    if (onRowClick) {
      onRowClick(record)
    }
  }

  const onTableChange: TableProps<InvoiceWithRelationsDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <TableExportButton
          data={response?.data}
          headers={[
            "Invoice #",
            "Member",
            "Amount",
            "Status",
            "Due Date",
            "Issued",
          ]}
          toRow={(invoice) => {
            const firstName = invoice.membership?.user?.firstName ?? ""
            const lastName = invoice.membership?.user?.lastName ?? ""
            const memberName = [firstName, lastName].filter(Boolean).join(" ")
            return [
              invoice.invoiceNumber,
              memberName,
              formatCurrency(invoice.totalAmount),
              invoice.status,
              invoice.dueAt ? formatDateUtc(invoice.dueAt) : "",
              invoice.issuedAt ? formatDateUtc(invoice.issuedAt) : "",
            ]
          }}
          filename="dues-invoices.csv"
        />
      </div>
      <Table<InvoiceWithRelationsDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: onRowClick ? "pointer" : "default" },
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
